import { useState, useEffect, useCallback, useRef } from "react";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  SpeakerWaveIcon,
  MusicalNoteIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  FilmIcon,
} from "@heroicons/react/24/outline";
import { IDialogueTone, IVideoGeneration, IAudioGeneration } from "@packages/global";
import { voiceApi } from "../../services/user/voice.api";
import { useDialog } from "../../hooks/useDialog";
import { toast } from "react-hot-toast";
import { convertAudioToMp3 } from "../../utils/audioConverter";
import AudioTrimmer from "../common/AudioTrimmer";
import AudioPlayer from "../common/AudioPlayer";
import AudioSelector from "../common/AudioSelector";
import VideoSelector from "../common/VideoSelector";
import { useTranslation } from 'react-i18next';
import { FFmpegCDNLoader } from "../../utils/ffmpegCDNLoader";
import { fetchFile } from "@ffmpeg/util";

const MIN_DURATION = 5;
const MAX_DURATION = 30;
const COMPRESS_THRESHOLD = 3 * 1024 * 1024;

type AudioSourceTab = 'upload' | 'audio' | 'video';

interface VoiceLibraryTabProps {
  modalOpen: boolean;
  onModalClose: () => void;
}

export default function VoiceLibraryTab({ modalOpen, onModalClose }: VoiceLibraryTabProps) {
  const { t } = useTranslation(['library', 'common']);
  const [voices, setVoices] = useState<IDialogueTone[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVoice, setEditingVoice] = useState<IDialogueTone | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    desc: "",
    previewAudioUrl: "",
    originalFileName: "",
    duration: 0,
  });
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [trimmerUrl, setTrimmerUrl] = useState<string | null>(null);
  const trimmerFileNameRef = useRef("");
  const [audioSourceTab, setAudioSourceTab] = useState<AudioSourceTab>('upload');
  const [showAudioSelector, setShowAudioSelector] = useState(false);
  const [showVideoSelector, setShowVideoSelector] = useState(false);
  const { confirm } = useDialog();

  useEffect(() => {
    if (modalOpen) {
      resetForm();
      setIsModalOpen(true);
      onModalClose();
    }
  }, [modalOpen]);

  const fetchVoices = useCallback(async () => {
    setLoading(true);
    try {
      const result = await voiceApi.getMyVoices({
        query: {},
        pagination: { page, limit: 12 },
      });
      setVoices(result.items);
      setTotalPages(result.pagination.totalPages);
    } catch (error) {
      toast.error(t('library:voice.toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchVoices(); }, [fetchVoices]);

  const uploadFile = async (file: File): Promise<{ url: string; duration: number }> => {
    const fd = new FormData();
    fd.append("audio", file);
    const response = await fetch("/api/user/upload/audio", {
      method: "POST",
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      body: fd,
    });
    const result = await response.json();
    if (!result.success || !result.data?.url) throw new Error(result.message || "上传失败");
    const duration = await new Promise<number>((resolve) => {
      const audio = new Audio(result.data.url);
      audio.onloadedmetadata = () => resolve(Math.round(audio.duration));
      audio.onerror = () => resolve(0);
    });
    return { url: result.data.url, duration };
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const allowedTypes = ["audio/mpeg", "audio/wav", "audio/flac", "audio/aiff"];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|flac|aif|aiff)$/i)) {
      toast.error(t('library:voice.validation.formatOnly'));
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error(t('library:voice.validation.sizeLimit'));
      return;
    }

    setUploading(true);
    setUploadStatus(t('library:voice.processing'));
    try {
      let converted: File = file;
      if (file.size > COMPRESS_THRESHOLD) {
        setUploadStatus(t('library:voice.compressing'));
        converted = await convertAudioToMp3(file, (p) => setUploadStatus(p.message));
      }

      const localUrl = URL.createObjectURL(converted);
      const rawDuration = await new Promise<number>((resolve) => {
        const audio = new Audio(localUrl);
        audio.onloadedmetadata = () => resolve(audio.duration);
        audio.onerror = () => resolve(0);
      });

      if (rawDuration < MIN_DURATION || rawDuration > MAX_DURATION) {
        trimmerFileNameRef.current = converted.name;
        setTrimmerUrl(localUrl);
        setUploading(false);
        setUploadStatus("");
        return;
      }

      setUploadStatus(t('library:voice.uploading'));
      const { url, duration } = await uploadFile(converted);
      URL.revokeObjectURL(localUrl);
      setFormData((prev) => ({ ...prev, previewAudioUrl: url, originalFileName: file.name, duration }));
    } catch (error: any) {
      toast.error(error.message || t('library:voice.toast.audioProcessFailed'));
    } finally {
      setUploading(false);
      setUploadStatus("");
    }
  };

  /** 从音频库选择 */
  const handleAudioSelect = async (audio: IAudioGeneration) => {
    if (!audio.resultAudioUrl) return;
    setShowAudioSelector(false);
    setUploading(true);
    setUploadStatus(t('library:voice.processing'));
    try {
      const duration = await new Promise<number>((resolve) => {
        const a = new Audio(audio.resultAudioUrl!);
        a.onloadedmetadata = () => resolve(Math.round(a.duration));
        a.onerror = () => resolve(0);
      });
      const fileName = audio.prompt ? `${audio.prompt.slice(0, 20)}.mp3` : 'audio.mp3';
      if (duration < MIN_DURATION || duration > MAX_DURATION) {
        trimmerFileNameRef.current = fileName;
        setTrimmerUrl(audio.resultAudioUrl!);
        return;
      }
      setFormData((prev) => ({ ...prev, previewAudioUrl: audio.resultAudioUrl!, originalFileName: fileName, duration }));
    } catch (err: any) {
      toast.error(err.message || t('library:voice.toast.audioProcessFailed'));
    } finally {
      setUploading(false);
      setUploadStatus("");
    }
  };

  /** 从视频提取音频 */
  const handleVideoSelect = async (video: IVideoGeneration) => {
    const videoUrl = video.resultVideoUrl;
    if (!videoUrl) return;
    setShowVideoSelector(false);
    setUploading(true);
    setUploadStatus(t('library:voice.processing'));
    try {
      const loader = new FFmpegCDNLoader((p) => setUploadStatus(p.message));
      const { ffmpeg } = await loader.loadFFmpeg();

      setUploadStatus(t('library:voice.extractingAudio'));
      const videoData = await fetchFile(videoUrl);
      await ffmpeg.writeFile('input.mp4', videoData);
      await ffmpeg.exec(['-i', 'input.mp4', '-vn', '-acodec', 'libmp3lame', '-ab', '192k', 'output.mp3']);

      const audioData = await ffmpeg.readFile('output.mp3');
      if (!audioData || (audioData as Uint8Array).length === 0) throw new Error('提取音频失败');

      const audioBlob = new Blob([new Uint8Array(audioData as Uint8Array)], { type: 'audio/mpeg' });
      const audioFile = new File([audioBlob], 'extracted_audio.mp3', { type: 'audio/mpeg' });

      try { await ffmpeg.deleteFile('input.mp4'); } catch { }
      try { await ffmpeg.deleteFile('output.mp3'); } catch { }
      ffmpeg.terminate();

      const localUrl = URL.createObjectURL(audioBlob);
      const duration = await new Promise<number>((resolve) => {
        const a = new Audio(localUrl);
        a.onloadedmetadata = () => resolve(Math.round(a.duration));
        a.onerror = () => resolve(0);
      });

      if (duration < MIN_DURATION || duration > MAX_DURATION) {
        trimmerFileNameRef.current = 'extracted_audio.mp3';
        setTrimmerUrl(localUrl);
        return;
      }

      setUploadStatus(t('library:voice.uploading'));
      const { url, duration: uploadedDuration } = await uploadFile(audioFile);
      URL.revokeObjectURL(localUrl);
      setFormData((prev) => ({ ...prev, previewAudioUrl: url, originalFileName: 'extracted_audio.mp3', duration: uploadedDuration }));
    } catch (err: any) {
      console.error('提取视频音频失败:', err);
      toast.error(err.message || t('library:voice.toast.audioProcessFailed'));
    } finally {
      setUploading(false);
      setUploadStatus("");
    }
  };

  const handleTrimConfirm = async (url: string) => {
    const duration = await new Promise<number>((resolve) => {
      const audio = new Audio(url);
      audio.onloadedmetadata = () => resolve(Math.round(audio.duration));
      audio.onerror = () => resolve(0);
    });
    if (trimmerUrl) URL.revokeObjectURL(trimmerUrl);
    setTrimmerUrl(null);
    setFormData((prev) => ({ ...prev, previewAudioUrl: url, originalFileName: trimmerFileNameRef.current, duration }));
  };

  const handleTrimCancel = () => {
    if (trimmerUrl) URL.revokeObjectURL(trimmerUrl);
    setTrimmerUrl(null);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.previewAudioUrl) {
      toast.error(t('library:voice.validation.nameAndAudioRequired'));
      return;
    }
    try {
      if (editingVoice) {
        await voiceApi.updateVoice(editingVoice.id, {
          name: formData.name,
          desc: formData.desc,
          previewAudioUrl: formData.previewAudioUrl || undefined,
          originalFileName: formData.originalFileName || undefined,
          duration: formData.duration || undefined,
        });
        toast.success(t('library:voice.toast.updateSuccess'));
      } else {
        await voiceApi.createVoice({
          name: formData.name,
          desc: formData.desc,
          previewAudioUrl: formData.previewAudioUrl,
          originalFileName: formData.originalFileName,
          duration: formData.duration,
        });
        toast.success(t('library:voice.toast.createSuccess'));
      }
      setIsModalOpen(false);
      resetForm();
      fetchVoices();
    } catch (error) {
      toast.error(t('library:voice.toast.operationFailed'));
    }
  };

  const handleEdit = (voice: IDialogueTone) => {
    setEditingVoice(voice);
    setFormData({
      name: voice.desc || voice.code,
      desc: voice.desc || "",
      previewAudioUrl: voice.previewAudioUrl || "",
      originalFileName: voice.originalFileName || "",
      duration: voice.duration || 0,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (voice: IDialogueTone) => {
    const confirmed = await confirm(t('library:voice.deleteConfirm'), t('common:confirmDelete'), 'danger');
    if (!confirmed) return;
    try {
      await voiceApi.deleteVoice(voice.id);
      toast.success(t('library:voice.toast.deleteSuccess'));
      fetchVoices();
    } catch (error) {
      toast.error(t('library:voice.toast.deleteFailed'));
    }
  };

  const resetForm = () => {
    setFormData({ name: "", desc: "", previewAudioUrl: "", originalFileName: "", duration: 0 });
    setEditingVoice(null);
    setAudioSourceTab('upload');
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : voices.length === 0 ? (
        <div className="text-center py-16">
          <MusicalNoteIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 mb-4">{t('library:voice.empty')}</p>
          <button
            onClick={() => { resetForm(); setIsModalOpen(true); }}
            className="text-primary hover:text-primary-light transition-colors cursor-pointer"
          >
            {t('library:voice.emptyAction')}
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {voices.map((voice) => (
              <div key={voice.id} className="bg-bg-surface border border-border-subtle rounded-lg p-4 hover:border-border-default transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-gray-50 font-medium truncate">{voice.desc || voice.code}</h3>
                    {voice.originalFileName && (
                      <p className="text-gray-500 text-sm truncate mt-0.5">{voice.originalFileName}</p>
                    )}
                  </div>
                  <div className="flex gap-1 ml-2 shrink-0">
                    <button onClick={() => handleEdit(voice)} className="p-1 text-gray-400 hover:text-gray-50 transition-colors cursor-pointer" title={t('common:edit')}>
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(voice)} className="p-1 text-gray-400 hover:text-red-400 transition-colors cursor-pointer" title={t('common:delete')}>
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {voice.previewAudioUrl && (
                  <div className="bg-bg-page rounded-md p-2">
                    <AudioPlayer src={voice.previewAudioUrl} />
                  </div>
                )}
                <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                  <span>{voice.duration ? formatDuration(voice.duration) : "--:--"}</span>
                  <span>{new Date(voice.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 bg-bg-elevated text-gray-300 rounded hover:bg-bg-surface disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer">{t('library:voice.pagination.prev')}</button>
              <span className="px-3 py-1 text-gray-400">{page} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 bg-bg-elevated text-gray-300 rounded hover:bg-bg-surface disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer">{t('library:voice.pagination.next')}</button>
            </div>
          )}
        </>
      )}

      {/* 添加/编辑弹窗 */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-bg-surface border border-border-subtle rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-gray-50">{editingVoice ? t('library:voice.edit') : t('library:voice.create')}</h2>
              <button onClick={() => { setIsModalOpen(false); resetForm(); }} className="p-1 text-gray-400 hover:text-gray-50 transition-colors cursor-pointer">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('library:voice.nameRequired')} <span className="text-primary">*</span></label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-bg-form border border-border-subtle rounded-lg text-gray-50 placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                  placeholder={t('library:voice.namePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('library:voice.description')}</label>
                <textarea
                  value={formData.desc}
                  onChange={(e) => setFormData({ ...formData, desc: e.target.value })}
                  className="w-full px-3 py-2 bg-bg-form border border-border-subtle rounded-lg text-gray-50 placeholder-gray-500 focus:outline-none focus:border-primary transition-colors resize-none"
                  placeholder={t('library:voice.descriptionPlaceholder')}
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  {t('library:voice.audioFileLabel')} <span className="text-primary">*</span>
                  <span className="text-gray-500 ml-2 font-normal">{t('library:voice.audioDurationHint', { min: MIN_DURATION, max: MAX_DURATION })}</span>
                </label>

                {formData.previewAudioUrl ? (
                  <div className="bg-bg-form border border-border-subtle rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-gray-400 text-sm truncate">{formData.originalFileName}</span>
                        {formData.duration > 0 && (
                          <span className="text-xs text-gray-500 shrink-0">{formatDuration(formData.duration)}</span>
                        )}
                      </div>
                      <button
                        onClick={() => setFormData({ ...formData, previewAudioUrl: "", originalFileName: "", duration: 0 })}
                        className="text-gray-400 hover:text-gray-50 cursor-pointer transition-colors ml-2"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <AudioPlayer src={formData.previewAudioUrl} />
                  </div>
                ) : (
                  <>
                    {/* 来源选项卡 */}
                    <div className="flex gap-1 mb-3 bg-bg-elevated rounded-lg p-1">
                      <button
                        type="button"
                        onClick={() => setAudioSourceTab('upload')}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${audioSourceTab === 'upload' ? 'bg-bg-surface text-gray-50' : 'text-gray-400 hover:text-gray-300'}`}
                      >
                        <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                        {t('library:voice.sourceUpload')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setAudioSourceTab('audio')}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${audioSourceTab === 'audio' ? 'bg-bg-surface text-gray-50' : 'text-gray-400 hover:text-gray-300'}`}
                      >
                        <SpeakerWaveIcon className="h-3.5 w-3.5" />
                        {t('library:voice.sourceAudio')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setAudioSourceTab('video')}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${audioSourceTab === 'video' ? 'bg-bg-surface text-gray-50' : 'text-gray-400 hover:text-gray-300'}`}
                      >
                        <FilmIcon className="h-3.5 w-3.5" />
                        {t('library:voice.sourceVideo')}
                      </button>
                    </div>

                    {/* 上传音频文件 */}
                    {audioSourceTab === 'upload' && (
                      <div className="border-2 border-dashed border-border-default hover:border-border-subtle rounded-lg p-6 text-center transition-colors">
                        <input type="file" accept="audio/mpeg,audio/wav,audio/flac" onChange={handleFileChange} className="hidden" id="voice-tab-audio-upload" disabled={uploading} />
                        <label htmlFor="voice-tab-audio-upload" className="cursor-pointer block">
                          {uploading ? (
                            <div className="space-y-2">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                              <p className="text-sm text-gray-400">{uploadStatus || t('library:voice.processing')}</p>
                            </div>
                          ) : (
                            <>
                              <ArrowUpTrayIcon className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                              <p className="text-sm text-gray-400">{t('library:voice.clickToUpload')}</p>
                              <p className="text-xs text-gray-500 mt-1">{t('library:voice.uploadHint')}</p>
                              <p className="text-xs text-gray-600 mt-0.5">{t('library:voice.uploadHintTrim', { min: MIN_DURATION, max: MAX_DURATION })}</p>
                            </>
                          )}
                        </label>
                      </div>
                    )}

                    {/* 从音频库选择 */}
                    {audioSourceTab === 'audio' && (
                      <div className="border-2 border-dashed border-border-default hover:border-border-subtle rounded-lg p-6 text-center transition-colors">
                        {uploading ? (
                          <div className="space-y-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                            <p className="text-sm text-gray-400">{uploadStatus || t('library:voice.processing')}</p>
                          </div>
                        ) : (
                          <button type="button" onClick={() => setShowAudioSelector(true)} className="cursor-pointer w-full">
                            <SpeakerWaveIcon className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                            <p className="text-sm text-gray-400">{t('library:voice.sourceAudioHint')}</p>
                            <p className="text-xs text-gray-500 mt-1">{t('library:voice.sourceAudioDesc')}</p>
                          </button>
                        )}
                      </div>
                    )}

                    {/* 从视频提取音频 */}
                    {audioSourceTab === 'video' && (
                      <div className="border-2 border-dashed border-border-default hover:border-border-subtle rounded-lg p-6 text-center transition-colors">
                        {uploading ? (
                          <div className="space-y-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                            <p className="text-sm text-gray-400">{uploadStatus || t('library:voice.processing')}</p>
                          </div>
                        ) : (
                          <button type="button" onClick={() => setShowVideoSelector(true)} className="cursor-pointer w-full">
                            <FilmIcon className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                            <p className="text-sm text-gray-400">{t('library:voice.sourceVideoHint')}</p>
                            <p className="text-xs text-gray-500 mt-1">{t('library:voice.sourceVideoDesc')}</p>
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setIsModalOpen(false); resetForm(); }} className="px-4 py-2 text-gray-400 hover:text-gray-50 transition-colors cursor-pointer">{t('common:cancel')}</button>
              <button onClick={handleSubmit} className="px-4 py-2 bg-gradient-to-br from-primary to-primary-light hover:shadow-[0_0_20px_rgba(255,92,0,0.3)] text-white rounded-lg transition-all cursor-pointer">
                {editingVoice ? t('common:save') : t('common:create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {trimmerUrl && (
        <AudioTrimmer
          audioUrl={trimmerUrl}
          minDuration={MIN_DURATION}
          maxDuration={MAX_DURATION}
          onConfirm={handleTrimConfirm}
          onCancel={handleTrimCancel}
        />
      )}

      <AudioSelector
        isOpen={showAudioSelector}
        onClose={() => setShowAudioSelector(false)}
        onSelect={handleAudioSelect}
      />

      <VideoSelector
        isOpen={showVideoSelector}
        onClose={() => setShowVideoSelector(false)}
        onSelect={handleVideoSelect}
        models={[]}
      />
    </>
  );
}
